import { TextButton } from '@creditornot/cb-button';
import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ImageCardSkeleton } from './ImageCardSkeleton';

type Props = {
  fetchNextPage: () => void;
  skeletonCount: number;
  isLoading: boolean;
};

const StyledTextButton = styled(TextButton)`
  justify-self: center;
  align-self: center;
`;

export const Pagination = ({ fetchNextPage, skeletonCount, isLoading }: Props) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  const supportsIntersectionObserver = 'IntersectionObserver' in window;

  useEffect(() => {
    if (!supportsIntersectionObserver || isLoading) {
      return;
    }

    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
      },
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [supportsIntersectionObserver, observerTarget, fetchNextPage, isLoading]);

  if (!supportsIntersectionObserver) {
    return (
      <StyledTextButton onClick={() => fetchNextPage()}>
        <FormattedMessage id="shared-menu.editor.image-gallery.show-more" />
      </StyledTextButton>
    );
  }

  return (
    <>
      <ImageCardSkeleton ref={observerTarget} />
      {Array.from({ length: skeletonCount }).map((_, index) => (
        <ImageCardSkeleton key={index} />
      ))}
    </>
  );
};
